/**
 * Social Hub Page
 * Unified page combining Trail Buddies and Groups into a tabbed interface
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Container,
  Typography,
  Fab,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { UserFacingFriendStatus } from '@api/types/social'
import { FriendsList } from '../components/FriendsList'
import { PendingRequestsList } from '../components/PendingRequestsList'
import { BlockedUsersList } from '../components/BlockedUsersList'
import { GroupsList } from '../components/GroupsList'
import { DiscoverGroups } from '../components/DiscoverGroups'
import { BlockedGroupsList } from '../components/BlockedGroupsList'
import { GroupInvitesList } from '../components/GroupInvitesList'
import { CreateGroupDialog } from '../components/CreateGroupDialog'

type PrimaryTab = 'buddies' | 'groups'
type BuddiesSub = 'friends' | 'pending' | 'blocked'
type GroupsSub = 'my-groups' | 'discover' | 'invites'

const BUDDIES_SUBS: { id: BuddiesSub; label: string }[] = [
  { id: 'friends', label: 'Friends' },
  { id: 'pending', label: 'Pending' },
  { id: 'blocked', label: 'Blocked' },
]

const GROUPS_SUBS: { id: GroupsSub; label: string }[] = [
  { id: 'my-groups', label: 'My Groups' },
  { id: 'discover', label: 'Discover' },
  { id: 'invites', label: 'Invites' },
]

export function SocialHubPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filterText, setFilterText] = useState('')
  const [createGroupOpen, setCreateGroupOpen] = useState(false)
  const [searchExpanded, setSearchExpanded] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Read tab state from URL
  const tabParam = searchParams.get('tab') as PrimaryTab | null
  const subParam = searchParams.get('sub')

  const activeTab: PrimaryTab = (tabParam === 'buddies' || tabParam === 'groups') ? tabParam : 'buddies'
  const activeBuddiesSub: BuddiesSub =
    (subParam === 'friends' || subParam === 'pending' || subParam === 'blocked') ? subParam : 'friends'
  const activeGroupsSub: GroupsSub =
    (subParam === 'my-groups' || subParam === 'discover' || subParam === 'invites') ? subParam : 'my-groups'

  // Fetch friends for count badge
  const { data: friendsData } = useQuery({
    queryKey: ['friends'],
    queryFn: socialApi.getFriends,
  })

  // Fetch groups for count badge
  const { data: groupsData } = useQuery({
    queryKey: ['my-groups'],
    queryFn: socialApi.getMyGroups,
  })

  // Fetch group invites for count badge
  const { data: invitesData } = useQuery({
    queryKey: ['group-invites'],
    queryFn: socialApi.getMyGroupInvites,
  })

  const friendsCount = friendsData?.filter(f => f.userFacingStatus === UserFacingFriendStatus.Connected).length || 0
  const pendingCount = friendsData?.filter(f =>
    f.userFacingStatus === UserFacingFriendStatus.Pending && !f.requestedByCurrentUser
  ).length || 0
  const groupsCount = groupsData?.length || 0
  const invitesCount = (invitesData as any[])?.length || 0

  const setTab = (tab: PrimaryTab, sub?: string) => {
    setFilterText('')
    const params: Record<string, string> = { tab }
    if (sub) params.sub = sub
    setSearchParams(params, { replace: true })
  }

  const setSub = (sub: string) => {
    setFilterText('')
    setSearchParams({ tab: activeTab, sub }, { replace: true })
  }

  const handleFabClick = () => {
    if (activeTab === 'groups') {
      setCreateGroupOpen(true)
    }
    // For buddies tab, could open add friend dialog in future
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 3 }, position: 'relative', minHeight: '80vh' }}>
      {/* Primary Tabs */}
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 0,
        }}
      >
        {(['buddies', 'groups'] as PrimaryTab[]).map((tab) => {
          const isActive = activeTab === tab
          const count = tab === 'buddies' ? friendsCount : groupsCount
          return (
            <Box
              key={tab}
              onClick={() => setTab(tab)}
              sx={{
                flex: 1,
                py: 1.25,
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: isActive ? 'primary.main' : 'text.secondary',
                cursor: 'pointer',
                borderBottom: '2px solid',
                borderBottomColor: isActive ? 'primary.main' : 'transparent',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                userSelect: 'none',
              }}
            >
              {tab === 'buddies' ? 'Buddies' : 'Groups'}
              {count > 0 && (
                <Box
                  sx={{
                    bgcolor: pendingCount > 0 && tab === 'buddies' ? 'error.main' : 'rgba(255,255,255,0.08)',
                    color: pendingCount > 0 && tab === 'buddies' ? 'white' : 'text.secondary',
                    fontSize: 10,
                    fontWeight: 700,
                    px: 0.75,
                    py: 0.125,
                    borderRadius: '10px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {count}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>

      {/* Sub-Tabs + Search */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 1.25, px: 0 }}>
        {(activeTab === 'buddies' ? BUDDIES_SUBS : GROUPS_SUBS).map((sub) => {
          const isActive = activeTab === 'buddies'
            ? activeBuddiesSub === sub.id
            : activeGroupsSub === sub.id

          // Count badges for sub-tabs
          let subCount: number | undefined
          if (activeTab === 'buddies') {
            if (sub.id === 'friends') subCount = friendsCount
            else if (sub.id === 'pending') subCount = pendingCount
          } else {
            if (sub.id === 'my-groups') subCount = groupsCount
            else if (sub.id === 'invites') subCount = invitesCount
          }

          return (
            <Box
              key={sub.id}
              onClick={() => setSub(sub.id)}
              sx={{
                py: 0.75,
                px: 1.75,
                borderRadius: '20px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 0.625,
                userSelect: 'none',
                ...(isActive
                  ? {
                      bgcolor: 'rgba(212,168,83,0.12)',
                      border: '1px solid rgba(212,168,83,0.3)',
                      color: 'primary.main',
                    }
                  : {
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid',
                      borderColor: 'divider',
                      color: 'text.secondary',
                    }),
              }}
            >
              {sub.label}
              {subCount !== undefined && subCount > 0 && (
                <Typography component="span" sx={{ fontSize: 10, opacity: 0.7 }}>
                  {subCount}
                </Typography>
              )}
            </Box>
          )
        })}

        {/* Search */}
        <Box sx={{ ml: 'auto' }}>
          {!isMobile ? (
            <TextField
              size="small"
              placeholder="Search..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              sx={{ width: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: filterText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilterText('')}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <IconButton onClick={() => setSearchExpanded(!searchExpanded)} size="small">
              {searchExpanded ? <ClearIcon /> : <SearchIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Mobile search expansion */}
      {isMobile && (
        <Collapse in={searchExpanded}>
          <Box sx={{ pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: filterText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilterText('')}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Collapse>
      )}

      {/* Content */}
      <Box sx={{ pt: 0.5 }}>
        {activeTab === 'buddies' && (
          <>
            {activeBuddiesSub === 'friends' && <FriendsList filterText={filterText} />}
            {activeBuddiesSub === 'pending' && <PendingRequestsList filterText={filterText} />}
            {activeBuddiesSub === 'blocked' && <BlockedUsersList filterText={filterText} />}
          </>
        )}
        {activeTab === 'groups' && (
          <>
            {activeGroupsSub === 'my-groups' && <GroupsList filterText={filterText} />}
            {activeGroupsSub === 'discover' && <DiscoverGroups filterText={filterText} />}
            {activeGroupsSub === 'invites' && <GroupInvitesList />}
          </>
        )}
      </Box>

      {/* FAB */}
      <Fab
        onClick={handleFabClick}
        sx={{
          position: 'fixed',
          bottom: { xs: 24, md: 32 },
          right: { xs: 24, md: 32 },
          width: 52,
          height: 52,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #d4a853, #c4943f)',
          color: '#1a1a2e',
          boxShadow: '0 4px 16px rgba(212,168,83,0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #e0be78, #d4a853)',
          },
        }}
      >
        <AddIcon />
      </Fab>

      {/* Create Group Dialog */}
      <CreateGroupDialog
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
      />
    </Container>
  )
}
